import React, { useContext, useEffect, useState } from "react";
import { FieldValues, FormProvider, useFormContext } from "react-hook-form";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { Form } from "forms/form.types";
import Section from "components/common/Section";
import RenderForm from "components/RenderForm";
import Button from "components/common/Button";
import MultiFormContext from "config/multiFormContext";
import styles from "./MultipleForm.module.scss";

interface MultipleFormsInterface {
  inputForms: Form[];
  onSubmit: (data: FieldValues) => void;
  reStartForm?: boolean;
  action?: React.ReactNode;
  isLoading?: boolean;
}

const MultipleForms: React.FC<MultipleFormsInterface> = ({
  inputForms,
  onSubmit,
  reStartForm = false,
  action = "",
  isLoading = false,
}) => {
  const form = useFormContext();
  const [currentForm, setcurrentForm] = useState(null as React.ReactNode);
  const [currentIndex, setCurrentIndex] = useState(0);
  const { setActiveForm } = useContext(MultiFormContext);

  const forms = inputForms.map((form) => {
    return (
      <div
        key={form.formTitle}
        className={inputForms[currentIndex] !== form ? `${styles.hidden}` : ""}
      >
        <Section title={form.formTitle} titleSize="large" action={action}>
          {form.formDescription && (
            <p className={styles["form-description"]}>{form.formDescription}</p>
          )}
          <RenderForm inputForm={form} />
        </Section>
      </div>
    );
  });

  const lastForm = currentIndex === forms.length - 1;
  const firstForm = currentIndex === 0;
  const sendLastForm = onSubmit;
  const typeOfSubmit = lastForm ? sendLastForm : () => {};

  useEffect(() => {
    setcurrentForm(forms[currentIndex]);
    setActiveForm(inputForms[currentIndex]);
    // eslint-disable-next-line
  }, [currentIndex]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentForm]);

  useEffect(() => {
    if (reStartForm) {
      setCurrentIndex(0);
      setcurrentForm(forms[0]);
    }
    // eslint-disable-next-line
  }, [reStartForm]);

  const moveNext = async () => {
    let tempCurrentIndex = currentIndex;
    const isValid = await form.trigger();
    if (isValid) {
      setCurrentIndex(tempCurrentIndex + 1);
      setcurrentForm(forms[tempCurrentIndex]);
    }
  };

  const moveBack = () => {
    form.unregister("googleMapLatLng");
    setCurrentIndex(currentIndex - 1);
    setcurrentForm(forms[currentIndex]);
  };

  const checkIfSubmitIsValid = async () => {
    const isValid = await form.trigger();
    if (isValid) {
      onSubmit(form.getValues());
    }
  };

  const BackButton = (
    <div className={styles["back-button"]}>
      <Button
        color="secondary"
        emphasis="high"
        curvature="high"
        action={moveBack}
      >
        <ArrowBackIosNewIcon fontSize="small" className={styles["back-icon"]} />
      </Button>
    </div>
  );

  const ContinueButton = (
    <div className={styles["right-button"]}>
      <Button
        color="secondary"
        emphasis="high"
        action={moveNext}
        curvature="high"
      >
        {"Siguiente"}
      </Button>
    </div>
  );

  const SubmitButton = isLoading ? (
    <div className={`${styles["right-button"]}`}>
      <Button action={() => void 0} disabled={true}>
        {"Enviando..."}
      </Button>
    </div>
  ) : (
    <div className={styles["right-button"]}>
      <Button
        color="primary"
        emphasis="high"
        curvature="high"
        action={checkIfSubmitIsValid}
      >
        {"Finalizar"}
      </Button>
    </div>
  );

  return (
    <FormProvider {...form}>
      <div className={styles.container}>
        <form
          onSubmit={form.handleSubmit(typeOfSubmit)}
          className={styles["form-container"]}
        >
          {currentForm}
          <div className={styles["buttons-container"]}>
            {!firstForm && BackButton}
            {lastForm && SubmitButton}
            {!lastForm && ContinueButton}
          </div>
        </form>
      </div>
    </FormProvider>
  );
};

export default MultipleForms;
